import type { FamilyDemand } from '@features/workshop-plan/types';

import { useMemo, useState } from 'react';
import { Zap, Truck, ChevronUp, ChevronDown } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { Button } from '@shared/components/ui/button';
import { StatusLabel } from '@shared/components/status-label';

import { useGetFamilyDemandQuery } from '@features/workshop-plan/api/workshop-plan-api';
import { CustomizationSessionDialog } from '@features/workshop-plan/components/customization-session-dialog';
import {
  ALL_TECHNIQUES,
  TechniqueFilter,
  matchesTechnique,
  useTechniqueFilter,
  getTechniqueOptions,
  getTechniqueDotClass,
  resolveTechniqueFilter,
} from '@features/customization-technique';

/** Safety net in case the realtime stream is cut. */
const REFRESH_INTERVAL_MS = 120_000;

const byTotalQuantityDesc = <T extends { inWorkshop: number; incoming: number }>(a: T, b: T) =>
  b.inWorkshop + b.incoming - (a.inWorkshop + a.incoming);

const GRID_CLASS = 'grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4';

type FamilyCardProps = {
  family: FamilyDemand;
  onOpen: (id: number) => void;
};

const FamilyCard = ({ family, onOpen }: FamilyCardProps) => (
  <li className="flex">
    <button
      aria-label={`${family.name} (${family.technique.name}) : ${family.inWorkshop} en atelier, ${family.incoming} à venir${family.express ? ', express' : ''}. Créer une session`}
      className={cn(
        'w-full rounded-2xl border-0 border-l-4 bg-card px-4 py-3 text-left text-card-foreground shadow-card transition-colors outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-primary',
        family.express ? 'border-l-destructive' : 'border-l-border'
      )}
      onClick={() => onOpen(family.id)}
      type="button"
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn('size-2.5 shrink-0 rounded-full', getTechniqueDotClass(family.technique.color))}
          title={family.technique.name}
        />
        <span className="min-w-0 flex-1 truncate text-base font-semibold" title={family.name}>
          {family.name}
        </span>
        {family.express && (
          <StatusLabel color="error" startIcon={<Zap />} variant="soft">
            Express
          </StatusLabel>
        )}
      </div>
      <div className="mt-3 flex items-end gap-6">
        <div className="flex flex-col">
          <span
            className={cn(
              'text-[2rem] leading-none font-bold tabular-nums',
              family.inWorkshop > 0 ? 'text-warning' : 'text-muted-foreground/70'
            )}
          >
            {family.inWorkshop}
          </span>
          <span className="text-xs text-muted-foreground">en atelier</span>
        </div>
        <div className="flex flex-col">
          <span className="text-lg leading-none font-bold text-muted-foreground tabular-nums sm:text-[19px]">
            {family.incoming}
          </span>
          <span className="text-xs text-muted-foreground">à venir</span>
        </div>
      </div>
      <IncomingOrders references={family.incomingReferences} />
    </button>
  </li>
);

/** Only the number of orders on their way: their references are listed in the session dialog. */
const IncomingOrders = ({ references }: { references: string[] }) => {
  if (references.length === 0) return null;

  return (
    <span className="mt-3 flex items-center gap-1.5 border-t border-border pt-3 text-sm font-medium text-muted-foreground">
      <Truck aria-hidden className="size-4 shrink-0" />
      {references.length} commande{references.length > 1 ? 's' : ''} à venir
    </span>
  );
};

/**
 * Product families to customize in the orders at the workshop, with the quantity still on its way. Kept up to date by
 * `useWorkshopOccupancy`. A family opens the creation of a customization session.
 *
 * Sorted by total quantity (in workshop + incoming). Families with nothing in the workshop yet are collapsed behind a
 * toggle to keep the screen light.
 */
export const FamilyDemandBar = () => {
  const { data: families } = useGetFamilyDemandQuery(undefined, {
    pollingInterval: REFRESH_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  });
  const [openedId, setOpenedId] = useState<number | null>(null);
  const [showWaiting, setShowWaiting] = useState(false);
  const [storedFilter, setFilter] = useTechniqueFilter();

  const techniques = useMemo(() => getTechniqueOptions(families ?? []), [families]);
  const filter = resolveTechniqueFilter(storedFilter, techniques);

  const { inWorkshop, waiting } = useMemo(() => {
    const sorted = (families ?? []).filter((family) => matchesTechnique(family, filter)).sort(byTotalQuantityDesc);
    return {
      inWorkshop: sorted.filter((family) => family.inWorkshop > 0),
      waiting: sorted.filter((family) => family.inWorkshop === 0),
    };
  }, [families, filter]);

  if (!families || families.length === 0) return null;

  const hasWaitingExpress = waiting.some((family) => family.express);

  const opened = families.find((family) => family.id === openedId);
  const filteredName = techniques.find((technique) => technique.id === filter)?.name;

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <span className="text-sm font-semibold text-muted-foreground">
            À personnaliser · choisir une famille pour lancer une session
          </span>
          {techniques.length > 0 && (
            <TechniqueFilter onChange={setFilter} options={techniques} unit="famille" value={filter} />
          )}
        </div>
        {inWorkshop.length === 0 && filter !== ALL_TECHNIQUES && (
          <p className="text-sm text-muted-foreground">Aucune famille en {filteredName} à l’atelier pour le moment.</p>
        )}
        {inWorkshop.length > 0 && (
          <ul aria-label="Familles de produits à personnaliser" className={GRID_CLASS}>
            {inWorkshop.map((family) => (
              <FamilyCard family={family} key={family.id} onOpen={setOpenedId} />
            ))}
          </ul>
        )}

        {waiting.length > 0 && (
          <>
            <Button
              aria-controls="family-demand-waiting"
              aria-expanded={showWaiting}
              className="self-start text-muted-foreground"
              onClick={() => setShowWaiting((value) => !value)}
              size="sm"
              variant="ghost"
            >
              {hasWaitingExpress && !showWaiting && <Zap className="text-destructive" />}
              {showWaiting
                ? 'Masquer les familles pas encore à l’atelier'
                : `Voir ${waiting.length} famille${waiting.length > 1 ? 's' : ''} pas encore à l’atelier${hasWaitingExpress ? ' (dont express)' : ''}`}
              {showWaiting ? <ChevronUp /> : <ChevronDown />}
            </Button>
            {showWaiting && (
              <ul
                aria-label="Familles de produits pas encore à l’atelier"
                className={GRID_CLASS}
                id="family-demand-waiting"
              >
                {waiting.map((family) => (
                  <FamilyCard family={family} key={family.id} onOpen={setOpenedId} />
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {opened && (
        <CustomizationSessionDialog
          familyId={opened.id}
          familyName={opened.name}
          incomingReferences={opened.incomingReferences}
          onClose={() => setOpenedId(null)}
        />
      )}
    </>
  );
};
